import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../slices/SnackbarSlice";
import {
  bulkDeleteReportsApi,
  bulkScheduleReportsApi,
  bulkSendReportsApi,
  createReportApi,
  deleteReportApi,
  embedMetaApi,
  getAiSummaryApi,
  getEligibleBusinessesApi,
  getPublicRankCompareApi,
  getPublicReportApi,
  getRankCompareApi,
  getRankScansApi,
  getReportApi,
  getReportAssigneesApi,
  getReportDataApi,
  listReportsApi,
  refreshAiSummaryApi,
  sendReportNowApi,
  syncRankBusinessesApi,
  updateEmailScheduleApi,
  updateReportApi,
} from "../apis/ReportBuilderApi";
import {
  EmbedKind,
  IEmailSchedulePayload,
  IKeywordGroup,
  IRankCompareParams,
  IReportListParams,
  IReportUpdatePayload,
  ISendNowPayload,
} from "../../components/ReportEditor/apiTypes";

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback;

// Read thunks stay silent so each screen decides how to show failures
const readThunk = <Arg, Res = any>(
  type: string,
  call: (arg: Arg) => Promise<{ data: Res }>,
  fallback: string,
) =>
  createAsyncThunk<Res, Arg, { rejectValue: string }>(
    type,
    async (arg, { rejectWithValue }) => {
      try {
        return (await call(arg)).data;
      } catch (error: any) {
        return rejectWithValue(errorMessage(error, fallback));
      }
    },
  );

// Write thunks surface the backend message through the snackbar; a caller that
// reports the outcome itself (inline in a modal, or in its own snackbar) mutes that side
interface IWriteSilence {
  success?: boolean;
  error?: boolean;
}

const writeThunk = <Arg, Res = any>(
  type: string,
  call: (arg: Arg) => Promise<{ data: Res }>,
  fallback: string,
  silence: IWriteSilence = {},
) =>
  createAsyncThunk<
    Res,
    Arg,
    { rejectValue: string; rejectedMeta: { status?: number } }
  >(
    type,
    async (arg, { rejectWithValue, dispatch }) => {
      try {
        const data = (await call(arg)) as { data: any };
        if (!silence.success) {
          dispatch(
            showSnackbar({
              message: data.data?.message || fallback,
              type: "success",
            }),
          );
        }
        return data.data;
      } catch (error: any) {
        const message = errorMessage(error, fallback);
        if (!silence.error) dispatch(showSnackbar({ message, type: "error" }));
        return rejectWithValue(message, { status: error?.response?.status });
      }
    },
  );

export const getEligibleBusinessesAction = readThunk<
  { search?: string; page?: number; limit?: number } | undefined
>(
  "reportBuilder/eligibleBusinesses",
  (params) => getEligibleBusinessesApi(params),
  "Failed to load businesses",
);

// Background backfill: every check-in business must have a rank tracker business
export const syncRankBusinessesAction = readThunk<void>(
  "reportBuilder/syncRankBusinesses",
  () => syncRankBusinessesApi(),
  "Failed to sync rank businesses",
);

export const listReportsAction =readThunk<IReportListParams | undefined>(
  "reportBuilder/list",
  (params) => listReportsApi(params),
  "Failed to load reports",
);

export const getReportAction = readThunk<string>(
  "reportBuilder/get",
  (id) => getReportApi(id),
  "Failed to load report",
);

export const getReportDataAction = readThunk<{
  id: string;
  sections?: string[];
  fresh?: boolean;
  period?: string;
  compare?: string;
}>(
  "reportBuilder/data",
  ({ id, sections, fresh, period, compare }) =>
    getReportDataApi(id, sections, fresh, { period, compare }),
  "Failed to load report data",
);

export const getRankScansAction = readThunk<{
  id: string;
  heatmapId: string;
}>(
  "reportBuilder/rankScans",
  ({ id, heatmapId }) => getRankScansApi(id, heatmapId),
  "Failed to load scan history",
);

export const getRankCompareAction = readThunk<{
  id: string;
  params: IRankCompareParams;
}>(
  "reportBuilder/rankCompare",
  ({ id, params }) => getRankCompareApi(id, params),
  "Failed to load rank comparison",
);

export const getReportAssigneesAction = readThunk<string>(
  "reportBuilder/assignees",
  (id) => getReportAssigneesApi(id),
  "Failed to load assignees",
);

// Silent so the embed form shows the backend's message under the link field
export const embedMetaAction = readThunk<{
  id: string;
  url: string;
  kind: EmbedKind;
}>(
  "reportBuilder/embedMeta",
  ({ id, url, kind }) => embedMetaApi(id, { url, kind }),
  "Could not check this link",
);

export const getAiSummaryAction = readThunk<void>(
  "reportBuilder/aiSummary",
  () => getAiSummaryApi(),
  "Failed to load AI summary",
);

export const getPublicReportAction = readThunk<string>(
  "reportBuilder/publicReport",
  (id) => getPublicReportApi(id),
  "Report not found",
);

export const getPublicRankCompareAction = readThunk<{
  id: string;
  params: IRankCompareParams;
}>(
  "reportBuilder/publicRankCompare",
  ({ id, params }) => getPublicRankCompareApi(id, params),
  "Failed to load rank comparison",
);

export const createReportAction = writeThunk<{ businessId: string }>(
  "reportBuilder/create",
  (payload) => createReportApi(payload),
  "Report created",
);

export const updateReportAction = writeThunk<{
  id: string;
  payload: IReportUpdatePayload;
}>(
  "reportBuilder/update",
  ({ id, payload }) => updateReportApi(id, payload),
  "Report updated",
);

// Autosave variant: the backend message is only surfaced when it fails
export const saveKeywordGroupsAction = createAsyncThunk<
  any,
  { id: string; keywordGroups: IKeywordGroup[] },
  { rejectValue: string }
>(
  "reportBuilder/saveKeywordGroups",
  async ({ id, keywordGroups }, { rejectWithValue, dispatch }) => {
    try {
      return (await updateReportApi(id, { rank: { keywordGroups } })).data;
    } catch (error: any) {
      const message = errorMessage(error, "Failed to save keyword groups");
      dispatch(showSnackbar({ message, type: "error" }));
      return rejectWithValue(message);
    }
  },
);

// Autosave of the keywords that drive the rank KPIs; the caller refetches the rankings section once it lands
export const saveKeywordSelectionAction = createAsyncThunk<
  any,
  { id: string; selectedHeatmapIds: string[] },
  { rejectValue: string }
>(
  "reportBuilder/saveKeywordSelection",
  async ({ id, selectedHeatmapIds }, { rejectWithValue, dispatch }) => {
    try {
      return (await updateReportApi(id, { rank: { selectedHeatmapIds } })).data;
    } catch (error: any) {
      const message = errorMessage(error, "Failed to save selected keywords");
      dispatch(showSnackbar({ message, type: "error" }));
      return rejectWithValue(message);
    }
  },
);

export const deleteReportAction = writeThunk<string>(
  "reportBuilder/delete",
  (id) => deleteReportApi(id),
  "Report deleted",
);

export const bulkDeleteReportsAction = writeThunk<{ ids: string[] }>(
  "reportBuilder/bulkDelete",
  (payload) => bulkDeleteReportsApi(payload),
  "Reports deleted",
);

export const updateEmailScheduleAction = writeThunk<{
  id: string;
  payload: IEmailSchedulePayload;
}>(
  "reportBuilder/emailSchedule",
  ({ id, payload }) => updateEmailScheduleApi(id, payload),
  "Email schedule saved",
  { error: true },
);

export const sendReportNowAction = writeThunk<{
  id: string;
  payload: ISendNowPayload;
}>(
  "reportBuilder/sendNow",
  ({ id, payload }) => sendReportNowApi(id, payload),
  "Report sent",
  { error: true },
);

export const bulkScheduleReportsAction = writeThunk<
  IEmailSchedulePayload & { ids: string[] }
>(
  "reportBuilder/bulkSchedule",
  (payload) => bulkScheduleReportsApi(payload),
  "Schedules updated",
  { success: true, error: true },
);

export const bulkSendReportsAction = writeThunk<{ ids: string[] }>(
  "reportBuilder/bulkSend",
  (payload) => bulkSendReportsApi(payload),
  "Bulk send finished",
  { success: true },
);

export const refreshAiSummaryAction = writeThunk<void>(
  "reportBuilder/aiSummaryRefresh",
  () => refreshAiSummaryApi(),
  "Summary refreshed",
);
